import { WORKSPACE } from "sharedConstants"
import WorkspaceJobModel from "models/workspaceJob.model"
import SelectionSettingsModel from "models/selectionSettings.model"
import { Map, List, fromJS } from "immutable"

/*
 * State need to be erased when user leaves the workspaces -> visits workspace
 * list page.
 *
 * state example:
 * Map({
 *   data: List([ WorkspaceJobModel ]),
 *   hasMoreItems: true/false,
 *   selectionSettings: SelectionSettingsModel(),
 *   isFetching: false
 * })
 */

const initialState = null

export default (state = initialState, { type, payload }) => {
  const addNewJobToExistingList = () => {
    const stateTemp = state
      .set("data", state.get("data").unshift(new WorkspaceJobModel(fromJS(payload.workspace_job))))
      .set("isFetching", false)

    if (state.get("hasMoreItems")) {
      return stateTemp.setIn(
        ["selectionSettings", "offset"],
        stateTemp.getIn(["selectionSettings", "offset"]) + 1
      )
    }
    return stateTemp
  }

  switch (type) {
    case `${WORKSPACE.JOB.ACTION.LIST}_PENDING`:
    case `${WORKSPACE.JOB.ACTION.RUN}_PENDING`:
    case `${WORKSPACE.JOB.ACTION.RETRIEVE}_PENDING`:
    case `${WORKSPACE.JOB.ACTION.CANCEL}_PENDING`:
      if (Map.isMap(state)) {
        return state.set("isFetching", true)
      } else {
        return Map({ isFetching: true })
      }

    case `${WORKSPACE.JOB.ACTION.LIST}_FULFILLED`:
      if (payload.selection_settings.offset === 0) {
        // initial load
        return Map({
          selectionSettings: new SelectionSettingsModel(payload.selection_settings),
          hasMoreItems: payload.workspace_jobs.length === payload.selection_settings.limit,
          data: List(payload.workspace_jobs.map(job => new WorkspaceJobModel(fromJS(job)))),
          isFetching: false
        })
      } else {
        // concat
        return state
          .set("selectionSettings", new SelectionSettingsModel(payload.selection_settings))
          .set("hasMoreItems", payload.workspace_jobs.length === payload.selection_settings.limit)
          .set(
            "data",
            state
              .get("data")
              .concat(payload.workspace_jobs.map(job => new WorkspaceJobModel(fromJS(job))))
          )
          .set("isFetching", false)
      }

    case WORKSPACE.JOB.ACTION.REFRESH_LAST:
      if (payload.workspace_job) {
        if (state === null) {
          return Map({
            selectionSettings: new SelectionSettingsModel({
              offset: 0,
              limit: 20
            }),
            hasMoreItems: false,
            data: List([new WorkspaceJobModel(fromJS(payload.workspace_job))])
          })
        } else {
          const lastSavedJob = state.getIn(["data", 0])
          if (lastSavedJob) {
            /*
             * Now three situations can occurs:
             * a) jobs are completely same (same -> they have same id & status) => do nothing
             * b) jobs are same, but have different statuses => replace the job
             * c) payload job is completely new => add to redux state on first position
             */
            if (
              payload.workspace_job.id === lastSavedJob.id &&
              payload.workspace_job.status !== lastSavedJob.status
            ) {
              // case b)
              return state.setIn(["data", 0], new WorkspaceJobModel(fromJS(payload.workspace_job)))
            }
            if (payload.workspace_job.id !== lastSavedJob.id) {
              // case c)
              return addNewJobToExistingList()
            }
            // case a)
            return state
          } else {
            return state.set("data", List([new WorkspaceJobModel(fromJS(payload.workspace_job))]))
          }
        }
      }
      return state

    case `${WORKSPACE.JOB.ACTION.RUN}_FULFILLED`:
      if (List.isList(state.get("data"))) {
        return addNewJobToExistingList()
      } else {
        return state.set("isFetching", false)
      }

    case WORKSPACE.JOB.ACTION.CLEAR_CACHE:
      return initialState

    case `${WORKSPACE.JOB.ACTION.RETRIEVE}_FULFILLED`:
    case WORKSPACE.JOB.ACTION.RETRIEVE:
      if (state === null || !List.isList(state.get("data"))) {
        return Map({
          selectionSettings: new SelectionSettingsModel({
            offset: 0,
            limit: 20
          }),
          hasMoreItems: false,
          data: List([new WorkspaceJobModel(fromJS(payload.workspace_job))]),
          isFetching: false
        })
      } else {
        const jobIndex = state.get("data").findIndex(job => job.id === payload.workspace_job.id)
        if (jobIndex !== -1) {
          return state
            .setIn(["data", jobIndex], new WorkspaceJobModel(fromJS(payload.workspace_job)))
            .set("isFetching", false)
        }
        return addNewJobToExistingList()
      }

    case `${WORKSPACE.JOB.ACTION.CANCEL}_FULFILLED`:
      const jobIndex = state.get("data").findIndex(job => job.id === payload.workspace_job.id)
      return state
        .setIn(["data", jobIndex], new WorkspaceJobModel(fromJS(payload.workspace_job)))
        .set("isFetching", false)

    default:
      return state
  }
}
